import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import type {
  ChangelogSectionKey,
  ChangelogSections,
} from "@/features/changelog/api/changelog.api";
import {
  buildTicketUrl,
  CHANGELOG_SECTION_ORDER,
} from "@/features/changelog/lib/changelog-display";

/** Each section keeps its own colour wherever notes are rendered. */
export const SECTION_CHIP_CLASS: Record<ChangelogSectionKey, string> = {
  new: "bg-primary-subtle text-primary-subtle-foreground",
  improved: "bg-[var(--ai-fill-chip)] text-[var(--ai-text)]",
  fixed: "bg-warning-subtle text-warning-subtle-foreground",
};

export function SectionChip({
  sectionKey,
  className,
}: {
  sectionKey: ChangelogSectionKey;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        "w-18 shrink-0 rounded-md py-0.5 text-center text-[10px]",
        SECTION_CHIP_CLASS[sectionKey],
        className,
      )}
    >
      {t(`admin.changelog.sections.${sectionKey}`, { defaultValue: sectionKey })}
    </span>
  );
}

/**
 * The notes as a reader sees them. Shared by the card and the send preview so
 * an admin cannot approve one rendering and ship another.
 */
export function ChangelogSectionList({ sections }: { sections: ChangelogSections }) {
  const populated = CHANGELOG_SECTION_ORDER.filter((key) => sections[key].length > 0);

  return (
    <>
      {populated.map((key) => (
        <div key={key} className="flex items-start gap-3">
          <SectionChip sectionKey={key} />
          <ul className="m-0 flex max-w-[68ch] list-disc flex-col gap-1 pl-4 text-xs leading-relaxed text-foreground">
            {sections[key].map((item, index) => (
              <li key={`${item.topic}-${index}`}>
                <span className="font-medium">{item.topic}:</span> {item.description}
                {item.ticket ? (
                  <>
                    {" "}
                    <a
                      href={buildTicketUrl(item.ticket)}
                      target="_blank"
                      rel="noreferrer"
                      className="whitespace-nowrap text-primary underline-offset-4 hover:underline"
                    >
                      {item.ticket}
                    </a>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}
